"use strict";

import dispatcher from "./../../dispatcher";
import sessionStore from "./../../store/session";
import poiCrossingStore from "./../../store/vehiclePOICrossings";
import displayedDetailsStore from "./../../store/displayedDetails";
import openedDetailsStore from "./../../store/openedDetails";

import Tooltip from "./Tooltip";
import zIndex from "./zIndex";

import {pointToLatLng} from "./../../utils/geo";
import {getColorOfPOI} from "./../../utils/poi";
import vehiclePOICrossingUtils from "./../../utils/vehiclePOICrossing";

const FOCUS_ZOOM = 16;

const POI_CROSSING_MARKER = {
	zIndex: zIndex.POI_CROSSING_MARKER,
	icon: {
		path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
		scale: 3,
		fillOpacity: 1,
		strokeWeight: 2,
		anchor: new google.maps.Point(0,4)
	}
};

const HIGHLIGHTED_POI_CROSSING_MARKER = {
	zIndex: zIndex.HIGHLIGHTED_POI_CROSSING_MARKER,
	icon: {
		path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
		scale: 3,
		fillOpacity: 1,
		strokeOpacity: 0.5,
		strokeWeight: 10,
		anchor: new google.maps.Point(0,4)
	}
};

export default class VehiclePOICrossingsController{
	
	constructor(map){
		this.map = map;
		this.initMarkers();
		this.setListeners();
	}
	
	initMarkers(){
		this.markers = {};
	}
	
	setListeners(){
		poiCrossingStore.on("insert", poiCrossingDescriptors => this.addPOICrossings(poiCrossingDescriptors));
		poiCrossingStore.on("remove", poiCrossingDescriptors => {
			poiCrossingDescriptors.forEach(descriptor => this.removePOICrossingsByVehicleId(descriptor.vehicle));
		});
		poiCrossingStore.on("update", poiCrossingDescriptor => this.updatePOICrossing(poiCrossingDescriptor));
		displayedDetailsStore.on("change", detailsDescriptor => this.handleDetailsStoreChange(detailsDescriptor));
	}
	
	handleDetailsStoreChange(detailsDescriptor){
		this.updateVisibilities();
		if(detailsDescriptor && openedDetailsStore.get(detailsDescriptor.id).activePage == "poiCrossings"){
			const poiCrossingsDescriptor = poiCrossingStore.get(detailsDescriptor.id);
		}
	}
	
	removePOICrossingsByVehicleId(vehicleId){
		if(!this.markers[vehicleId]){
			return;
		}
		
		for(let poiId in this.markers[vehicleId]){
			for(let time in this.markers[vehicleId][poiId]){
				this.markers[vehicleId][poiId][time].setMap(null);
			}
		}
	}
	
	addPOICrossings(poiCrossingsDescriptors){
		poiCrossingsDescriptors.forEach(
			poiCrossingDescriptor => this.addPOICrossing(poiCrossingDescriptor)
		);
	}
	
	addPOICrossing(descriptor){
		if(this.markers[descriptor.vehicle]){
			this.removePOICrossingsByVehicleId(descriptor.vehicle);
			this.markers[descriptor.vehicle] = {};
		}
		else{
			this.markers[descriptor.vehicle] = {};
		}
		
		descriptor.result.crossings.forEach(
			crossing => {
				if(!this.markers[descriptor.vehicle][crossing.poi]){
					this.markers[descriptor.vehicle][crossing.poi] = {};
				}
				this.markers[descriptor.vehicle][crossing.poi][crossing.time] = this.createMarker(descriptor.vehicle, crossing);
			}
		);
		
		vehiclePOICrossingUtils.focusOnVehiclePOICrossings(descriptor.vehicle);
	}
	
	removeHighlightedMarker(){
		if(this.highlightedMarker){
			this.highlightedMarker.setMap(null);
			this.highlightedMarker = null;
		}
	}
	
	updateHighlightedPOICrossing(poiCrossingDescriptor){
		this.removeHighlightedMarker();
		
		if(poiCrossingDescriptor.highlightedCrossing){
			const poiCrossing = vehiclePOICrossingUtils.getPOICrossingByTime(poiCrossingDescriptor.vehicle, poiCrossingDescriptor.highlightedCrossing);
			
			if(poiCrossing){
				this.createHighlightedMarker(poiCrossing);
			}
		}
	}
	
	updatePOICrossing(poiCrossingDescriptor){
		if(!poiCrossingDescriptor){
			return;
		}
		
		this.updateVisibility(poiCrossingDescriptor.vehicle);
		this.updateHighlightedPOICrossing(poiCrossingDescriptor);
	}
	
	updateVisibilities(){
		this.removeHighlightedMarker();
		for(let vehicleId in this.markers){
			this.updateVisibility(vehicleId);
		}
	}
	
	updateVisibility(vehicleId){
		const markers = this.markers[vehicleId];
		const descriptor = poiCrossingStore.get(vehicleId);
		
		if(!descriptor){
			return;
		}
		
		const canBeDisplayed = vehiclePOICrossingUtils.getVisibilityOfPOICrossings(vehicleId);
		
		for(let poiId in markers){
			const pinned = descriptor.pinnedCrossings.has(poiId);
			const shouldBeDisplayed = canBeDisplayed && pinned;
			
			for(let time in markers[poiId]){
				if(shouldBeDisplayed != markers[poiId][time].getVisible()){
					markers[poiId][time].setVisible(shouldBeDisplayed);
				}	
			}
		}
	}
	
	createHighlightedMarker(crossing){
		this.highlightedMarker = new google.maps.Marker({
			map: this.map,
			position: pointToLatLng(crossing.location)
		});
		
		const options = HIGHLIGHTED_POI_CROSSING_MARKER;
		options.icon.fillColor = getColorOfPOI(crossing.poi);
		options.icon.strokeColor = getColorOfPOI(crossing.poi);
		options.icon.rotation = crossing.heading;
		
		this.highlightedMarker.setOptions(options);
	}
	
	handleEventmarkerMouseover(vehicleId, crossing){
		dispatcher.dispatch("highlightVehiclePOICrossing", vehicleId, crossing.time);
		
		const tooltipId = Date.now();
		const position = pointToLatLng(crossing.location);
		const icon = crossing.inside ? "login" : "logout";
		const label = new Date(crossing.time).toLocaleString();
		dispatcher.dispatch("createTooltip", tooltipId, position, label, icon);
		
		google.maps.event.addListenerOnce(this.highlightedMarker, "mouseout", _ => {
			dispatcher.dispatch("removeTooltip", tooltipId);
			dispatcher.dispatch("unhighlightVehiclePOICrossing", vehicleId);
		});
	}
	
	createMarker(vehicleId, crossing){
		const dateString = new Date(crossing.time).toLocaleDateString();
		
		const marker = new google.maps.Marker({
			map: this.map,
			position: pointToLatLng(crossing.location)
		});
		
		const options = POI_CROSSING_MARKER;
		options.icon.fillColor = getColorOfPOI(crossing.poi);
		options.icon.strokeOpacity = 0;
		options.icon.rotation = crossing.heading;
		
		marker.setOptions(options);
		
		google.maps.event.addListener(marker, "mouseover", evt => this.handleEventmarkerMouseover(vehicleId, crossing));		
		
		return marker;
	}
}


// WEBPACK FOOTER //
// ./src/view/Map/VehiclePOICrossingsController.js